
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateAdapterOptions } from '@angular/material-moment-adapter';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';

import { WavelengthUiModule } from 'wavelength-ui';

//// Third party
import { NgxMatDatetimepicker, NgxMatDatepickerInput, NgxMatTimepickerComponent } from '@imario42/datetime-picker';
import {
  NgxMatMomentModule,
  NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  NgxMatMomentDateAdapterOptions
} from '@imario42/moment-adapter';

import { MessageDialogComponent } from './message-dialog/message-dialog.component';
import { DesignerValidationErrorsComponent } from '../common/designer-validation-errors/designer-validation-errors.component';
import { ValueControlComponent } from './value-control/value-control.component';
import { NumberboxComponent } from './numberbox/numberbox.component';
import { DateboxComponent } from './datebox/datebox.component';
import { TimeboxComponent } from './timebox/timebox.component';
import { ValidNameValidatorDirective } from './directives/valid-name.directive';
import { JsonformatterDirective } from './directives/jsonformatter.directive';
import { FillPipe } from './directives/fill.pipe';
import { OverlayComponent } from './overlay/overlay.component';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatCardModule } from '@angular/material/card';
import { DATEX_APP_API_URL } from './services/application.service';
import { environment } from '../../environments/environment';
import { CdkTableModule } from '@angular/cdk/table';
import { GridComponent } from './grid/grid.component';
import { MonacoEditorComponent } from './monaco-editor/monaco-editor.component';
import { CronTimePickerComponent } from './cron-time-picker/cron-time-picker.component';
import { CronEditorComponent } from './cron-editor/cron-editor.component';
import { ApiConnectionControlComponent } from './api-connection-control/api-connection-control.component';
import { CronPipe } from './directives/cron.pipe';
import { ConfirmStringDialogComponent } from './confirm-string-dialog/confirm-string-dialog.component';
import { AutoFocusDirective } from '../common/directives/autofocus.directive';
import { CodeInputComponent } from './code-editor/code-input/code-input.component';
import { ExprInputComponent } from './code-editor/expr-input/expr-input.component';

import { AngularSignaturePadModule } from '@almothafar/angular-signature-pad';
import { ImageViewerModule } from './image-viewer/image-viewer.module';
import { DateFormatPipe } from './directives/date-format.pipe';
import { TimePeriodPipe } from './directives/time-period.pipe';
import { CopilotAnalysisComponent } from './code-editor/copilot-analysis/copilot-analysis.component';
import { OperationSelectFormComponent } from './operation-select-form/operation-select-form.component';
import { RoleSelectFormComponent } from './role-select-form/role-select-form.component';
import { DependencyCollisionInfoComponent } from './dependency-collision-info/dependency-collision-info.component';
import { RequestsInterceptor } from '../interceptors/requests.interceptor';
import { UniqueSettingNameValidatorDirective } from '../studio/common/directives/unique-setting-name.directive';
import { ForbiddenNameValidatorDirective } from './directives/forbidden-name.directive';
import { ValidEmailValidatorDirective } from './directives/valid-email';
import { DesignerInformationComponent } from './designer-information/designer-information.component';
import { MarkdownViewerComponent } from './markdown-viewer/markdown-viewer.component';
import { CodeReviewModalComponent } from './code-review-modal/code-review-modal.component';

// AG GRID
import { AgGridAngular } from '@ag-grid-community/angular';
import { AgGridCellDisplayControlDef, AgGridColumnDef, AgGridComponent } from './ag-grid/ag-grid.component';
import { AgGridRowOptionsCellRenderer } from './ag-grid/ag-grid-row-options.component';
import { AgGridCustomCellRenderer } from './ag-grid/ag-grid-cell-renderer.component';
import { EntityAssignmentColumnDef } from '../management/newversion/common/entity-assignment/entity-assignment-column-def.directive';

// AG CHART
import { AgCharts, AgGauge } from 'ag-charts-angular';

import { DATEX_IS_PRODUCTION } from 'wavelength-ui';
import { HelpButtonComponent } from './help-button/help-button.component';
import { UserTileComponent } from '../management/newversion/users/user-tile/user-tile.component';


const momentDateAdapterConfig: MatMomentDateAdapterOptions = {
  useUtc: true
};

const ngxMomentDateAdapterConfig: NgxMatMomentDateAdapterOptions = {
  useUtc: true
};

@NgModule({
  declarations: [
    MessageDialogComponent,
    ConfirmStringDialogComponent,
    DesignerValidationErrorsComponent,
    DesignerInformationComponent,
    ValueControlComponent,
    ApiConnectionControlComponent,
    NumberboxComponent,
    DateboxComponent,
    TimeboxComponent,
    ValidNameValidatorDirective,
    ValidEmailValidatorDirective,
    ForbiddenNameValidatorDirective,
    UniqueSettingNameValidatorDirective,
    JsonformatterDirective,
    FillPipe,
    CronPipe,
    DateFormatPipe,
    TimePeriodPipe,
    OverlayComponent,
    GridComponent,
    AgGridColumnDef,
    AgGridCellDisplayControlDef,
    EntityAssignmentColumnDef,
    AgGridCustomCellRenderer,
    AgGridRowOptionsCellRenderer,
    AgGridComponent,
    MonacoEditorComponent,
    CronEditorComponent,
    CronTimePickerComponent,
    AutoFocusDirective,
    ExprInputComponent,
    CodeInputComponent,
    CopilotAnalysisComponent,
    OperationSelectFormComponent,
    RoleSelectFormComponent,
    DependencyCollisionInfoComponent,
    HelpButtonComponent,
    UserTileComponent,
    MarkdownViewerComponent,
    CodeReviewModalComponent
  ],
  exports: [
    // Re-exporting modules so when other modules import the sharedmodule
    // they have access to those module as well
    // https://angular.io/guide/sharing-ngmodules
    ImageViewerModule,
    CommonModule,
    FormsModule,
    WavelengthUiModule,
    AngularSignaturePadModule,
    LoadingBarHttpClientModule,
    DragDropModule,
    MatCardModule,
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatListModule,
    MatMomentDateModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTableModule,
    CdkTableModule,
    AgGridAngular,
    AgCharts,
    AgGauge,
    NgxMatDatetimepicker,
    NgxMatDatepickerInput,
    NgxMatTimepickerComponent,
    NgxMatMomentModule,
    MessageDialogComponent,
    ConfirmStringDialogComponent,
    DesignerValidationErrorsComponent,
    DesignerInformationComponent,
    ValueControlComponent,
    ApiConnectionControlComponent,
    NumberboxComponent,
    DateboxComponent,
    TimeboxComponent,
    ValidNameValidatorDirective,
    ValidEmailValidatorDirective,
    ForbiddenNameValidatorDirective,
    UniqueSettingNameValidatorDirective,
    JsonformatterDirective,
    FillPipe,
    CronPipe,
    DateFormatPipe,
    TimePeriodPipe,
    OverlayComponent,
    GridComponent,
    AgGridColumnDef,
    AgGridCellDisplayControlDef,
    EntityAssignmentColumnDef,
    AgGridComponent,
    MonacoEditorComponent,
    ReactiveFormsModule,
    CronEditorComponent,
    CronTimePickerComponent,
    AutoFocusDirective,
    ExprInputComponent,
    CodeInputComponent,
    OperationSelectFormComponent,
    RoleSelectFormComponent,
    HelpButtonComponent,
    UserTileComponent,
    MarkdownViewerComponent,
    CodeReviewModalComponent
  ],
  imports: [
    ImageViewerModule.forRoot({
      containerBackgroundColor: '#fff',
      allowFullscreen: true,
      btnIcons: {
        fullscreen: 'icon icon-ic_fluent_full_screen_maximize_20_regular',
        zoomOut: 'icon icon-ic_fluent_subtract_20_regular',
        zoomIn: 'icon icon-ic_fluent_add_20_regular',
        rotateCounterClockwise: 'icon icon-ic_fluent_arrow_rotate_counterclockwise_20_regular',
        rotateClockwise: 'icon icon-ic_fluent_arrow_rotate_clockwise_20_regular'
      },
      btnShow: {
        zoomIn: true,
        zoomOut: true,
        rotateClockwise: true,
        rotateCounterClockwise: true
      }
    }),
    AngularSignaturePadModule,
    CommonModule,
    FormsModule,
    WavelengthUiModule,
    LoadingBarHttpClientModule,
    DragDropModule,
    MatCardModule,
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatListModule,
    MatMomentDateModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTableModule,
    CdkTableModule,
    NgxMatDatetimepicker,
    NgxMatDatepickerInput,
    NgxMatTimepickerComponent,
    NgxMatMomentModule,
    ReactiveFormsModule,
    AgGridAngular,
    AgCharts,
    AgGauge
  ],
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: momentDateAdapterConfig },
    { provide: NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: ngxMomentDateAdapterConfig },
    { provide: DATEX_APP_API_URL, useValue: environment.datexApplicationApiUrl },
    { provide: HTTP_INTERCEPTORS, useClass: RequestsInterceptor, multi: true },
    { provide: DATEX_IS_PRODUCTION, useValue: environment.production },
    provideHttpClient(withInterceptorsFromDi()),
  ]
})
export class SharedModule { }
